import { formatAnalyticsCurrency } from "../shared/analyticsFormatting";
import type { MetricCardTrend } from "../shared/MetricCard";
import type {
  OfferConversionResponse,
  OfferConversionRow,
  OrdersByDayResponse,
  RatingsByStayResponse,
} from "@/types/analytics-overview";
import type { TotalRevenueResponse } from "@/types/analytics-revenue";

export type OverviewKpiMetric = {
  title: string;
  value: string;
  delta?: string;
  trend?: MetricCardTrend;
};

const DELTA_SUFFIX = " from previous period";

export function averageOfferConversionRate(
  totals: OfferConversionRow[] | undefined,
): number {
  if (!totals?.length) {
    return 0;
  }

  const periodTotals = totals.filter((row) => row.day === "TOTAL");
  const rates = periodTotals
    .map((row) => row.conversionRate)
    .filter((rate): rate is number => rate !== null);

  if (!rates.length) {
    return 0;
  }

  const sum = rates.reduce((acc, rate) => acc + rate, 0);
  return Math.round((sum / rates.length) * 10) / 10;
}

export function getTotalRevenueAmount(
  response: TotalRevenueResponse | undefined,
): number {
  if (!response?.totals.byCurrency.length) {
    return 0;
  }

  const divisor = response.amountDivisor || 1;
  const netMinor = response.totals.byCurrency.reduce(
    (sum, row) => sum + row.net,
    0,
  );

  return netMinor / divisor;
}

export function formatPercentValue(value: number): string {
  return `${value.toLocaleString("en-US", { maximumFractionDigits: 1 })}%`;
}

export function formatRatingValue(value: number | null | undefined): string {
  const normalized = value ?? 0;

  return normalized.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatOrdersValue(count: number | undefined): string {
  const normalized = count ?? 0;
  const label = normalized === 1 ? "service" : "services";
  return `${normalized.toLocaleString("en-US")} ${label}`;
}

export function formatRevenueValue(amount: number): string {
  return formatAnalyticsCurrency(amount, {
    maximumFractionDigits: 2,
    symbol: "€",
    symbolPosition: "suffix",
  });
}

function percentChange(current: number, previous: number): number | null {
  if (current === previous) {
    return 0;
  }

  if (previous === 0) {
    return current === 0 ? 0 : 100;
  }

  return ((current - previous) / previous) * 100;
}

export function getRatingComparisonValue(
  totals: RatingsByStayResponse["totals"] | undefined,
): number {
  if (!totals?.feedbackCount) {
    return 0;
  }

  return totals.avgRating ?? 0;
}

function formatPercentDelta(change: number | null): string | undefined {
  if (change === null) {
    return undefined;
  }

  const sign = change > 0 ? "+" : "";
  const formatted = change.toLocaleString("en-US", {
    maximumFractionDigits: 1,
  });

  return `${sign}${formatted}%${DELTA_SUFFIX}`;
}

function trendFromChange(change: number | null): MetricCardTrend {
  if (change === null || change === 0) {
    return "neutral";
  }

  return change > 0 ? "positive" : "negative";
}

function buildPercentMetricDelta(
  current: number,
  previous: number,
): Pick<OverviewKpiMetric, "delta" | "trend"> {
  const change = percentChange(current, previous);

  return {
    delta: formatPercentDelta(change),
    trend: trendFromChange(change),
  };
}

export function buildOverviewMetricDelta(
  current: number | null | undefined,
  previous: number | null | undefined,
): Pick<OverviewKpiMetric, "delta" | "trend"> {
  return buildPercentMetricDelta(current ?? 0, previous ?? 0);
}

export function buildOverviewKpiMetrics(input: {
  offerConversion?: OfferConversionResponse;
  ratingsByStay?: RatingsByStayResponse;
  ordersByDay?: OrdersByDayResponse;
  revenueTotal?: TotalRevenueResponse;
  previousOfferConversion?: OfferConversionResponse;
  previousRatingsByStay?: RatingsByStayResponse;
  previousOrdersByDay?: OrdersByDayResponse;
  previousRevenueTotal?: TotalRevenueResponse;
  includeDeltas: boolean;
}): OverviewKpiMetric[] {
  const currentRevenue = getTotalRevenueAmount(input.revenueTotal);
  const previousRevenue = getTotalRevenueAmount(input.previousRevenueTotal);

  const currentConversion = averageOfferConversionRate(
    input.offerConversion?.totals,
  );
  const previousConversion = averageOfferConversionRate(
    input.previousOfferConversion?.totals,
  );

  const currentRating = getRatingComparisonValue(input.ratingsByStay?.totals);
  const previousRating = getRatingComparisonValue(
    input.previousRatingsByStay?.totals,
  );

  const currentOrders = input.ordersByDay?.totals.orderCount ?? 0;
  const previousOrders = input.previousOrdersByDay?.totals.orderCount ?? 0;

  const revenueDelta = input.includeDeltas
    ? buildPercentMetricDelta(currentRevenue, previousRevenue)
    : {};
  const conversionDelta = input.includeDeltas
    ? buildPercentMetricDelta(currentConversion, previousConversion)
    : {};
  const ratingDelta = input.includeDeltas
    ? buildPercentMetricDelta(currentRating, previousRating)
    : {};
  const ordersDelta = input.includeDeltas
    ? buildPercentMetricDelta(currentOrders, previousOrders)
    : {};

  return [
    {
      title: "Service revenue",
      value: formatRevenueValue(currentRevenue),
      ...revenueDelta,
    },
    {
      title: "Conversion of offers",
      value: formatPercentValue(currentConversion),
      ...conversionDelta,
    },
    {
      title: "Avg. Guest Rating for Stay",
      value: formatRatingValue(currentRating),
      ...ratingDelta,
    },
    {
      title: "Orders for stay",
      value: formatOrdersValue(currentOrders),
      ...ordersDelta,
    },
  ];
}
